import React from 'react'
import { useModal, useSearch } from 'hooks'
import { DEFAULT_JOB_SEEKER_STATE } from '../../helpers'
import { useDispatch, useSelector } from 'react-redux'
import { candidates } from 'orm'
import { getCurrentAccount } from 'ducks/auth'
import { useCandidateState } from 'components/pages/Jobs/JobDetails/hooks'

export const useResultsCareer = () => {
  const dispatch = useDispatch()
  const [search, setSearch] = useSearch()

  const {
    state: { candidate },
    handlers: { setCandidate },
  } = useCandidateState()

  const {
    isOpen: isOverviewModalOpen,
    handleOpen: handleOverviewModalOpen,
    handleClose: handleOverviewModalClose,
  } = useModal()

  const {
    isOpen: isDeleteJobSeekerOpen,
    handleOpen: handleDeleteJobSeekerOpen,
    handleClose: handleDeleteJobSeekerClose,
  } = useModal()

  const handleResetJobSeeker = React.useCallback(
    () => setCandidate(DEFAULT_JOB_SEEKER_STATE),
    [setCandidate],
  )

  const handleOverviewModalCloseWithReset = React.useCallback(() => {
    handleResetJobSeeker()
    handleOverviewModalClose()
  }, [handleResetJobSeeker, handleOverviewModalClose])

  const handleDeleteJobSeekerCloseWithReset = React.useCallback(() => {
    handleResetJobSeeker()
    handleDeleteJobSeekerClose()
  }, [handleResetJobSeeker, handleDeleteJobSeekerClose])

  const account = useSelector(getCurrentAccount)
  const workspaceId = account?.talent_assessment_workspace?.id

  const handleDeleteJobSeeker = React.useCallback(
    (jobSeeker, { postSuccessAction }: { postSuccessAction?: any } = {}) => {
      dispatch(
        candidates.deleteStart({
          pk: jobSeeker.id,
          postSuccessAction,
        }),
      )
    },
    [dispatch],
  )

  const handleCopy = () => {
    console.log('handleCopy')
  }

  const handleDelete = React.useCallback(
    jobSeeker => {
      setCandidate(jobSeeker)
      handleDeleteJobSeekerOpen()
    },
    [setCandidate, handleDeleteJobSeekerOpen],
  )

  const handleEdit = React.useCallback(
    jobSeeker => {
      setCandidate(jobSeeker)
      handleOverviewModalOpen()
    },
    [setCandidate, handleOverviewModalOpen],
  )

  const handleDeleteJobSeekerConfirm = React.useCallback(() => {
    handleDeleteJobSeeker(candidate, {
      postSuccessAction: () => {
        handleResetJobSeeker()
        handleDeleteJobSeekerClose()
      },
    })
  }, [
    candidate,
    handleDeleteJobSeeker,
    handleDeleteJobSeekerClose,
    handleResetJobSeeker,
  ])

  const handleOverviewModalConfirm = React.useCallback(
    (data, { onError, onSuccess }: { onError?: any; onSuccess?: any } = {}) => {
      const isEditing = Boolean(candidate)

      const payload = {
        ...data,
        talent_assessment_workspace: workspaceId,
      }

      dispatch(
        isEditing
          ? candidates.patchStart({
              pk: candidate.id,
              data: payload,
              onFailureAction: onError,
              postSuccessAction: () => {
                onSuccess?.()
                handleOverviewModalCloseWithReset()
              },
            })
          : candidates.postStart({
              data: payload,
              onFailureAction: onError,
              postSuccessAction: () => {
                onSuccess?.()
                handleOverviewModalCloseWithReset()
              },
            }),
      )
    },
    [dispatch, candidate, workspaceId, handleOverviewModalCloseWithReset],
  )

  return {
    state: {
      candidate,
      search,
      isOverviewModalOpen,
      isDeleteJobSeekerOpen,
    },
    handlers: {
      handleOverviewModalOpen,
      handleOverviewModalClose: handleOverviewModalCloseWithReset,
      handleOverviewModalConfirm,
      handleDeleteJobSeekerClose: handleDeleteJobSeekerCloseWithReset,
      handleDeleteJobSeekerConfirm,
      handleCopy,
      handleDelete,
      handleEdit,
      setSearch,
    },
  }
}
